/**
 * Per-tour Photography row layout: which comparison-grid row (space) each
 * gallery image belongs to. Kept out of gallery.json so arranging rows is a
 * single small write instead of one tourGalleryUpdate per image (GCS allows
 * roughly one write per second to the same object).
 *
 * Storage: gs://{bucket}/tours/{tourId}/photo-rows.json
 *   { rows: { [imageId]: rowId }, updatedAt }
 *
 * Read and written directly with the Storage SDK, like space-variants.json.
 */

import {
  getBytes,
  getStorage,
  ref as storageRef,
  uploadString,
} from 'firebase/storage';

export type PhotoRowMap = Record<string, string>;

function objectPath(tourId: string): string {
  const id = tourId.startsWith('tours/') ? tourId.slice(6) : tourId;
  return `tours/${id}/photo-rows.json`;
}

export async function getTourPhotoRows(tourId: string): Promise<PhotoRowMap> {
  try {
    const bytes = await getBytes(storageRef(getStorage(), objectPath(tourId)));
    const data = JSON.parse(new TextDecoder().decode(bytes));
    const rows: PhotoRowMap = {};
    if (data?.rows && typeof data.rows === 'object') {
      Object.entries(data.rows).forEach(([imageId, rowId]) => {
        if (typeof rowId === 'string') rows[imageId] = rowId;
      });
    }
    return rows;
  } catch (err) {
    if ((err as { code?: string })?.code === 'storage/object-not-found') {
      return {};
    }
    throw err;
  }
}

export async function setTourPhotoRows(
  tourId: string,
  rows: PhotoRowMap,
): Promise<void> {
  await uploadString(
    storageRef(getStorage(), objectPath(tourId)),
    JSON.stringify({ rows, updatedAt: new Date().toISOString() }),
    'raw',
    { contentType: 'application/json', cacheControl: 'no-cache' },
  );
}
